import React, { useCallback } from 'react';
import InputBase from '../InputBase';
import styled from 'styled-components/macro';
import FieldTitle from '../FieldTitle';
import formatDate from '../../../utils/formatDate';
import DateFieldEngine from '../engine/DateFieldEngine';
import NumberFieldEngine from '../engine/NumberFieldEngine';
import { useField } from '../FormContext';
import useTranslations from '../../../hooks/useTranslations';
import DropDownFieldEngine from '../engine/DropDownFieldEngine';
import CurrencyComboFieldEngine from '../engine/CurrencyComboFieldEngine';

const SubFieldsContainer = styled.div`
  margin: 0 0 0 2rem;
  position: relative;
`;

const LineItem = styled.div`
  border-left: 1px solid #c4c4c4 !important;
  left: -1.5rem !important;
  position: absolute;
  width: 1rem;
  top: ${props => (props.first ? '0.5rem' : '-2.5rem')};
  bottom: 14px;
  border-bottom: 1px solid #c4c4c4 !important;
  .EUF-error && {
    bottom: 2.5rem;
  }
`;

const ErrorMsg = styled.p`
  padding: 0;
  font-size: 14px;
`;

const CurrencyConversionSubField = ({
  calculating,
  data,
  baseCurrency,
  index,
  subFieldValue,
  setSubFieldValue,
  onClickSubField,
  onFocus,
  onBlur,
  isParentReadOnly
}) => {
  let readOnly, defaultValue;
  const NUMBER_OF_FIRST_FIELDS_TO_HIDE = 2;
  const t = useTranslations();
  const validator = useCallback(
    required => value => {
      value = value || value === 0 ? value : '';
      value += '';
      let error = '';

      if (required && value.trim() === '') {
        error = t('forms.fields.error.required');
      }

      return error;
    },
    []
  );
  readOnly = !data.lookupInfo.subFieldInfo.editable || isParentReadOnly;
  defaultValue =
    data.defaultValue &&
    data.defaultValue.properties &&
    data.defaultValue.properties.value
      ? data.defaultValue.properties.value
      : undefined;
  if (data.uiType !== 'date') {
    useField(data.localFormFieldId, validator(data.required));
  }
  return (
    <div
      field-id={data.localFormFieldId}
      value={subFieldValue}
      style={{
        position: 'relative'
      }}
      onClick={onClickSubField}
    >
      <div
        style={{
          position: 'relative',
          display: index < NUMBER_OF_FIRST_FIELDS_TO_HIDE ? 'none' : 'block'
        }}
      >
        <LineItem first={index === NUMBER_OF_FIRST_FIELDS_TO_HIDE} />
        <FieldTitle
          title={data.title}
          isReadOnly={readOnly}
          isRequired={data.required}
        />
        {data.uiType === 'number' ? (
          <NumberFieldEngine
            readOnly={readOnly}
            internalId={data.localFormFieldId}
            setValue={v => setSubFieldValue(index, v)}
            value={subFieldValue}
            inputProps={{ readOnly: readOnly }}
            onFocus={() => {
              onFocus(index);
            }}
            onBlur={() => {
              onBlur(index);
            }}
            uiType="number"
            type="number"
          />
        ) : data.uiType === 'currency' && !calculating ? (
          <NumberFieldEngine
            readOnly={readOnly}
            internalId={data.localFormFieldId}
            setValue={v => setSubFieldValue(index, v)}
            value={subFieldValue}
            inputProps={{ readOnly: readOnly }}
            onFocus={() => {
              onFocus(index);
            }}
            onBlur={() => {
              onBlur(index);
            }}
            options={[baseCurrency]}
            uiType="currency"
            type="number"
          />
        ) : data.uiType === 'dropdown' ? (
          <DropDownFieldEngine
            disabled={readOnly}
            internalId={data.localFormFieldId}
            setValue={v => setSubFieldValue(index, v)}
            value={subFieldValue || defaultValue}
            inputProps={{ readOnly: readOnly }}
            options={data.options || []}
            onFocus={() => {
              onFocus(index);
            }}
            onBlur={() => {
              onBlur(index);
            }}
          />
        ) : data.uiType === 'date' ? (
          <DateFieldEngine
            disableFuture
            readOnly={readOnly}
            internalId={data.localFormFieldId}
            setValue={v => setSubFieldValue(index, v)}
            value={subFieldValue}
            inputProps={{ readOnly: readOnly }}
            onFocus={() => {
              onFocus(index);
            }}
            onBlur={() => {
              onBlur(index);
            }}
          />
        ) : (
          <InputBase
            disabled={readOnly}
            internalId={data.localFormFieldId}
            variant="outlined"
            value="Calculating..."
            inputProps={{ readOnly: readOnly }}
            onChange={e => setSubFieldValue(index, e.target.value)}
            onFocus={() => {
              onFocus(index);
            }}
            onBlur={() => {
              onBlur(index);
            }}
          />
        )}
        <ErrorMsg $uiType={data.uiType} data-error={data.internalId} />
      </div>
    </div>
  );
};

const CurrencyConversionFieldView = props => {
  return (
    <div>
      <CurrencyComboFieldEngine
        baseCurrency={props.baseCurrency}
        currencySelectValue={props.subFieldValues[0]}
        numberValue={props.subFieldValues[1]}
        currencySelectSubFieldIndex={0}
        numberValueSubFieldIndex={1}
        subFieldValues={props.subFieldValues}
        setSubFieldValue={props.setSubFieldValue}
        currencyOptions={
          props.subFields && props.subFields.length
            ? props.subFields[0].options
            : []
        }
        readOnly={props.readOnly}
      />
      <SubFieldsContainer>
        {props.subFields.map((one, k) => (
          <CurrencyConversionSubField
            key={k}
            baseCurrency={props.baseCurrency}
            calculating={one.uiType === 'currency' && props.calculating}
            index={k}
            isParentReadOnly={props.readOnly}
            data={one}
            subFieldValue={props.subFieldValues[k]}
            setSubFieldValue={props.setSubFieldValue}
            onClickSubField={props.onClickSubField}
            onFocus={props.onSubFieldFocus}
            onBlur={props.onSubFieldBlur}
          />
        ))}
      </SubFieldsContainer>
    </div>
  );
};

export default CurrencyConversionFieldView;
